import { makeAutoObservable, runInAction } from 'mobx'
import { api } from '@/shared/api/endpoints'
import { ApiError } from '@/shared/api/http'
import type { BalanceItem, Currency, WithdrawalResponse } from '@/shared/api/types'
import { coinOf } from '@/shared/lib/currencies'
import {
  addDecimal, checkAmount, compareDecimal, floorToScale, normalizeAmount, subtractDecimal,
} from '@/shared/lib/decimal'
import { crypto } from '@/shared/lib/format'
import { explain } from '@/shared/lib/errors'
import type { CabinetStore } from '@/stores/CabinetStore'
import type { UiStore } from '@/stores/UiStore'

export type WithdrawStep = 'form' | 'confirm' | 'done'

/** Сколько секунд кнопка подтверждения остаётся заблокированной. */
export const CONFIRM_DELAY = 1

/**
 * Монеты, у которых комиссия сети берётся СВЕРХ суммы: получатель получает
 * ровно заказанное, с баланса уходит сумма плюс комиссия. Тот же список,
 * что `fee_on_top` на бекенде.
 */
const FEE_ON_TOP: ReadonlySet<Currency> = new Set<Currency>([
  'btc', 'ltc', 'usdt_trc20', 'gram', 'usdt_ton',
])

export function feeOnTop(currency: Currency): boolean {
  return FEE_ON_TOP.has(currency)
}

/**
 * Заявка на вывод.
 *
 * Три вещи, ради которых это отдельный стор, а не состояние компонента:
 *
 * 1. **Ключ идемпотентности живёт дольше запроса.** Он создаётся один
 *    раз на попытку и НЕ меняется при повторе. Иначе оборванная связь
 *    и нажатое второй раз «Подтвердить» — это два перевода на один
 *    и тот же адрес. Ключ сбрасывается только после успеха или
 *    осознанной отмены.
 * 2. **Подтверждение отдельным шагом.** Сумма и адрес показываются
 *    ещё раз, и кнопка секунду неактивна: рефлекторный двойной
 *    клик по «Отправить» не должен доводить до подписи.
 * 3. **Размонтирование не отменяет заявку.** Компонент может уйти
 *    (перерисовка, переход), запрос — нет.
 */
export class WithdrawStore {
  currency: Currency | null = null
  /**
   * Адрес — свободное поле, а не кошелёк из списка. Так в макете и так
   * решил заказчик: сохранённые кошельки нужны автовыводам. Проверка
   * контрольной суммы сети — на бекенде, а здесь только «не пусто»:
   * держать во фронте разбор шести сетей значит однажды разойтись
   * с тем, что проверяет подписант.
   */
  address = ''
  amount = ''
  step: WithdrawStep = 'form'
  errors: { amount?: string; address?: string } = {}
  busy = false
  /** Созданная заявка — показывается на экране «готово». */
  created: WithdrawalResponse | null = null
  /** Осталось секунд до разблокировки «Подтвердить». */
  countdown = 0
  /**
   * Комиссия сети сверх суммы — ровно та, что покажется и уйдёт на сервер.
   * `null` — ещё не посчитана или не посчиталась.
   */
  fee: string | null = null

  private key: string | null = null
  private timer: ReturnType<typeof setInterval> | null = null
  private feeTimer: ReturnType<typeof setTimeout> | null = null
  /** Номер последнего запроса комиссии: ответ на устаревший адрес отбрасывается. */
  private feeRequest = 0
  private feeProblem: unknown = null

  private readonly cabinet: CabinetStore
  private readonly ui: UiStore

  constructor(cabinet: CabinetStore, ui: UiStore) {
    this.cabinet = cabinet
    this.ui = ui
    makeAutoObservable<
      this, 'cabinet' | 'ui' | 'key' | 'timer' | 'feeTimer' | 'feeRequest' | 'feeProblem'
    >(this, {
      cabinet: false, ui: false, key: false, timer: false,
      feeTimer: false, feeRequest: false, feeProblem: false,
    })
  }

  /**
   * Начать заново: каждое открытие окна — чистая форма.
   *
   * Раньше повторное открытие той же валюты возвращало прошлый адрес
   * и шаг. Так адрес BTC из прошлого вывода подставился в форму USDT
   * TRC20 — одно невнимательное «Подтвердить», и перевод ушёл бы
   * в чужую сеть. Не сбрасываем только идущую отправку: её ключ
   * идемпотентности должен дожить до ответа сервера.
   */
  start(currency: Currency): void {
    if (this.busy) return
    this.stopTimer()
    this.currency = currency
    this.address = ''
    this.amount = ''
    this.step = 'form'
    this.errors = {}
    this.created = null
    this.key = null
    this.fee = null
    this.feeProblem = null
    // Сразу, без адреса: нужна кнопке «весь баланс минус комиссия».
    void this.loadFee()
  }

  /**
   * Запросить комиссию под текущие валюту и адрес. Возвращает её же.
   *
   * Невалидный адрес в процессе набора — не повод для всплывашки: комиссия
   * просто не показана, а объяснение появится при нажатии «Отправить».
   */
  async loadFee(): Promise<string | null> {
    const currency = this.currency
    if (currency === null) return null
    if (!feeOnTop(currency)) {
      this.fee = '0'
      return '0'
    }
    const seq = ++this.feeRequest
    try {
      const { fee } = await api.money.withdrawalFee(currency, this.address || undefined)
      if (seq === this.feeRequest) {
        runInAction(() => { this.fee = fee })
        this.feeProblem = null
      }
      return fee
    } catch (cause) {
      if (seq === this.feeRequest) {
        runInAction(() => { this.fee = null })
        this.feeProblem = cause
      }
      return null
    }
  }

  private scheduleFee(): void {
    if (this.feeTimer !== null) clearTimeout(this.feeTimer)
    this.feeTimer = setTimeout(() => { void this.loadFee() }, 400)
  }

  get balance(): BalanceItem | null {
    const items = this.cabinet.balances.value?.items ?? []
    return items.find((item) => item.currency === this.currency) ?? null
  }

  setAddress(value: string): void {
    // Пробелы по краям — след копирования из мессенджера, не часть адреса.
    this.address = value.trim()
    const { address: _drop, ...rest } = this.errors
    this.errors = rest
    // Комиссия зависит от получателя: перевод USDT на адрес без USDT
    // стоит вдвое дороже. Пересчёт — после паузы в наборе.
    this.scheduleFee()
  }

  setAmount(value: string): void {
    this.amount = value
    const { amount: _drop, ...rest } = this.errors
    this.errors = rest
  }

  /**
   * Вписать весь баланс минус комиссия сети.
   *
   * Для монет с комиссией сверху (BTC, LTC, USDT-TRC20) с баланса уйдёт
   * сумма плюс комиссия, поэтому «всё» — это доступное без неё. Остальные —
   * доступное целиком: у них комиссия не добавляется к списанию.
   */
  async useMax(): Promise<void> {
    const available = this.balance?.available
    const currency = this.currency
    if (available === undefined || currency === null) return
    const fee = await this.loadFee()
    if (fee === null) {
      this.ui.failure(explain(this.feeProblem, coinOf(currency).ticker))
      return
    }
    const diff = subtractDecimal(available, fee)
    const rest = diff === null ? null : floorToScale(diff, currency)
    const positive = rest !== null && (compareDecimal(rest, '0') ?? 0) > 0
    this.setAmount(positive ? rest : '0')
  }

  private validate(): boolean {
    const errors: { amount?: string; address?: string } = {}
    if (this.address === '') errors.address = 'Укажите адрес'

    const currency = this.currency
    const problem = currency === null ? 'Выберите валюту' : checkAmount(this.amount, currency)
    if (problem) errors.amount = problem
    else {
      const available = this.balance?.available
      // Сравнение строками, не числами, и это не перестраховка.
      // У Monero двенадцать знаков после запятой; как только целая
      // часть доходит до пяти цифр, значащих разрядов набирается
      // больше шестнадцати, и double перестаёт различать соседние
      // суммы: Number('99999.123456789013') === Number('99999.123456789012').
      // Заявка на сумму больше баланса прошла бы проверку и встала
      // бы у подписанта — с уже списанным балансом.
      if (available !== undefined) {
        // С баланса уходит сумма ПЛЮС комиссия сверху — сравниваем их вместе.
        const total = addDecimal(normalizeAmount(this.amount), this.fee ?? '0')
        const diff = total === null ? null : compareDecimal(total, available)
        if (diff !== null && diff > 0) errors.amount = 'Больше доступного баланса'
      }
      // Порог сети проверяем ЗДЕСЬ же, а не только на бекенде.
      //
      // Отказ сервера мерчант получает после нажатия «Отправить» —
      // то есть узнаёт о минимуме, уже введя сумму и подтвердив адрес.
      // Число приходит с бекенда вместе с балансом: комиссия сети
      // живёт там, и держать её копию во фронте значит однажды
      // разойтись с ней.
      const minimum = this.balance?.min_withdrawal
      if (errors.amount === undefined && minimum !== undefined) {
        const low = compareDecimal(normalizeAmount(this.amount), minimum)
        if (low !== null && low < 0) {
          errors.amount = `Меньше минимума: от ${crypto(minimum)}`
        }
      }
    }

    this.errors = errors
    return Object.keys(errors).length === 0
  }

  /**
   * Перейти к подтверждению. Ключ идемпотентности рождается здесь.
   *
   * Комиссия пересчитывается под введённый адрес ДО проверки суммы:
   * без неё нечем сравнить «сумма + комиссия» с балансом.
   */
  async review(): Promise<void> {
    const currency = this.currency
    if (currency !== null && feeOnTop(currency)) {
      if (this.feeTimer !== null) clearTimeout(this.feeTimer)
      const fee = await this.loadFee()
      if (fee === null) {
        const text = explain(this.feeProblem, coinOf(currency).ticker)
        const badAddress = this.feeProblem instanceof ApiError
          && this.feeProblem.code === 'invalid_address'
        this.errors = badAddress ? { address: text } : { amount: text }
        return
      }
    }
    if (!this.validate()) return
    if (this.key === null) this.key = newKey()
    this.step = 'confirm'
    this.startTimer()
  }

  back(): void {
    this.stopTimer()
    this.step = 'form'
  }

  /**
   * Отмена на шаге подтверждения — единственное место, где ключ
   * сбрасывается до успеха. Человек сказал «нет», значит следующая
   * попытка — это уже другая заявка, а не повтор этой.
   */
  cancel(): void {
    this.stopTimer()
    this.key = null
    this.step = 'form'
  }

  async submit(): Promise<void> {
    if (this.busy || this.step !== 'confirm' || this.countdown > 0) return
    const currency = this.currency
    const key = this.key
    if (currency === null || this.address === '' || key === null) return
    const onTop = feeOnTop(currency)
    if (onTop && this.fee === null) return

    this.busy = true
    try {
      const created = await api.money.createWithdrawal(
        currency, this.address, normalizeAmount(this.amount), key,
        onTop ? this.fee ?? undefined : undefined,
      )
      runInAction(() => {
        this.created = created
        this.step = 'done'
        // Заявка ушла — ключ отработал. Новый вывод получит свой.
        this.key = null
      })
      this.stopTimer()
      await Promise.all([this.cabinet.balances.load(), this.cabinet.overview.load()])
      this.ui.success('Заявка на вывод создана')
    } catch (cause) {
      // Ключ НЕ трогаем: человек нажмёт «Повторить», и это будет
      // тот же самый запрос, а не второй перевод.
      if (cause instanceof ApiError && cause.code === 'fee_changed') {
        // Комиссия выросла, пока человек читал экран. Показываем новую
        // и снова даём секунду — подтверждать надо уже её, а не старую.
        const fresh = cause.details['fee']
        runInAction(() => {
          if (typeof fresh === 'string' && fresh !== '') this.fee = fresh
        })
        this.startTimer()
      }
      this.ui.failure(explain(cause, this.currency ? coinOf(this.currency).ticker : undefined))
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  private startTimer(): void {
    this.stopTimer()
    this.countdown = CONFIRM_DELAY
    this.timer = setInterval(() => {
      runInAction(() => {
        this.countdown -= 1
        if (this.countdown <= 0) this.stopTimer()
      })
    }, 1000)
  }

  private stopTimer(): void {
    if (this.timer !== null) clearInterval(this.timer)
    this.timer = null
    this.countdown = 0
  }

  dispose(): void {
    this.stopTimer()
    if (this.feeTimer !== null) clearTimeout(this.feeTimer)
    this.feeTimer = null
  }
}

/**
 * Ключ идемпотентности. `crypto.randomUUID` есть везде, кроме
 * незащищённого http — там откат на случайную строку: без ключа
 * бекенд не примет заявку вовсе.
 */
function newKey(): string {
  try {
    return globalThis.crypto.randomUUID()
  } catch {
    const bytes = new Uint8Array(16)
    globalThis.crypto.getRandomValues(bytes)
    return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
  }
}
